import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSearch } from "react-icons/fa";
import { FaHeartCircleMinus, FaRegEye } from "react-icons/fa6";
import TableLoader from "Components/Common/Loader/TableLoader";
import Swal from "sweetalert2";
import Loader from "../../../assets/img/extras/HeartLoader.gif";
import GangActivityModal from "./components/GangActivityModal";
import { usePermission } from "Context/PermissionContext";
import Pagination from "Components/Pagination/Pagination";
import { TbHeartPlus } from "react-icons/tb";

const GangSummary = () => {
  const [summary, setSummary] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activityModal, setActivityModal] = useState({
    open: false,
    gangInfo: null,
  });
  // Filters
  const [filters, setFilters] = useState({
    type: "",
    from: "",
    to: "",
    sortKey: "totalRanksEarned",
    sortOrder: -1,
    groupLanguage: "",
    limit: 5,
    sumType: "sum",
  });
  const [limit, setLimit] = useState(filters.limit);

  const [isLoading, setIsLoading] = useState(false);
  const [actionLoader, setActionLoader] = useState({
    loading: false,
    index: null,
  });
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.gangRankings?.subModule?.gangSummary?.permissions;

  const languages = [
    "English",
    "Hindi",
    "Bengali",
    "Telugu",
    "Marathi",
    "Tamil",
    "Urdu",
    "Gujarati",
    "Kannada",
    "Odia",
    "Malayalam",
    "Punjabi",
    "Assamese",
    "Maithili",
    "Sanskrit",
    "Konkani",
  ];

  const fetchSummary = async (initial = true) => {
    try {
      if (initial) {
        setIsLoading(true);
      }

      const params = {
        page,
        type: filters.type,
        sortKey: filters.sortKey,
        sortOrder: filters.sortOrder,
        groupLanguage: filters.groupLanguage,
        limit: filters.limit,
        sumType: filters.sumType,
      };

      if (filters.type === "Custom") {
        params.from = filters.from;
        params.to = filters.to;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/gang-summary/get-summary-of-all-gangs`,
        { params }
      );
      setSummary(res.data.gangs);
      setTotalPages(res.data.totalPages);
    } catch (error) {
      console.log("Error fetching summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    fetchSummary();
    setLimit(filters.limit);
  };

  const updateHeartVerification = (isHeartVerified, groupCallId, index) => {
    setActionLoader(() => ({
      loading: true,
      index: index,
    }));
    let config = {
      method: "put",
      url: `${process.env.REACT_APP_API_URL}/group-call/heart-verification/${groupCallId}`,
      data: {
        isHeartVerified: isHeartVerified,
      },
    };

    axios
      .request(config)
      .then((response) => {
        // console.log("Response of updateHeartVerification: ", response.data);
        fetchSummary(false);
        Swal.fire({
          icon: "success",

          title: `Gang Heart Verification`,
          text: `Gang marked as ${
            isHeartVerified ? "heart-verified" : "not heart-verified"
          } successfully`,
          timer: 2000,
          showConfirmButton: true,
          confirmButtonColor: "#a10f5b",
        });
      })
      .catch((error) => {
        console.log("Error in updateHeartVerification: ", error);
      })
      .finally(() => {
        setActionLoader(() => ({
          loading: false,
          index: null,
        }));
      });
  };

  function minutesToHM(totalMinutes) {
    if (totalMinutes < 60) {
      return `${totalMinutes} min${totalMinutes !== 1 ? "s" : ""}`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${minutes} min${minutes !== 1 ? "s" : ""}`;
  }

  return (
    <div className="p-6">
      <div className="mb-6 rounded-xl bg-white p-5 shadow-md">
        <h2 className="mb-4 text-2xl font-semibold">Gang Summary</h2>

        {/* Filters */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {/* Type */}
          <div>
            <label className="mb-1 block font-medium">Duration</label>
            <select
              name="type"
              className="w-full rounded-md border p-2"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="">Select</option>
              <option value="Weekly">Weekly (7 days)</option>
              <option value="Monthly">Monthly (30 days)</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* Custom From */}
          {filters.type === "Custom" && (
            <>
              <div>
                <label className="mb-1 block font-medium">From</label>
                <input
                  type="date"
                  name="from"
                  value={filters.from}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border p-2"
                />
              </div>

              <div>
                <label className="mb-1 block font-medium">To</label>
                <input
                  type="date"
                  name="to"
                  value={filters.to}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border p-2"
                />
              </div>
            </>
          )}
          {/* Group Language */}
          <div>
            <label className="mb-1 block font-medium">Group Language</label>
            <select
              name="groupLanguage"
              className="w-full rounded-md border p-2"
              value={filters.groupLanguage}
              onChange={handleFilterChange}
            >
              <option value="">Select Language</option>
              {languages.map((lang) => (
                <option value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          {/* Sort Key */}
          <div>
            <label className="mb-1 block font-medium">Sort By</label>
            <select
              name="sortKey"
              className="w-full rounded-md border p-2"
              value={filters.sortKey}
              onChange={handleFilterChange}
            >
              <option value="totalRanksEarned">Ranks Earned</option>
              <option value="totalActiveHours">Active Hours</option>
              <option value="totalGamesCompleted">Games Completed</option>
              <option value="totalGiftsReceived">Gifts Received</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="mb-1 block font-medium">Sort Order</label>
            <select
              name="sortOrder"
              className="w-full rounded-md border p-2"
              value={filters.sortOrder}
              onChange={handleFilterChange}
            >
              <option value={-1}>Descending</option>
              <option value={1}>Ascending</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block font-medium">Sort By</label>
            <select
              name="sumType"
              className="w-full rounded-md border p-2"
              value={filters.sumType}
              onChange={handleFilterChange}
            >
              <option value="sum">Sum</option>
              <option value="avg">Average</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block font-medium">Showing results </label>
            <input
              type="number"
              name="limit"
              className="w-full rounded-md border p-2"
              value={filters.limit}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <button
          onClick={applyFilters}
          className="mt-4 flex items-center gap-2 rounded-lg bg-[#c51369] px-4 py-2 text-white shadow hover:bg-[#a10f5b]"
        >
          <FaSearch /> Apply Filters
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-t-2xl">
        {isLoading ? (
          <TableLoader />
        ) : (
          <table className="w-full rounded-t-2xl text-left text-sm">
            <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
              <tr className="border-b">
                <th className="px-4 py-3 text-center">Sl No.</th>
                <th className="px-6 py-3 text-center">Gang Details</th>
                <th className="whitespace-normal px-6 py-3 text-center">
                  Active Hours(AH)
                </th>
                <th className="px-4 py-3 text-center">Games Completed(GC)</th>
                <th className="px-4 py-3 text-center">Gifts Received(GR)</th>
                <th className="px-4 py-3 text-center">Total Ranks</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {summary?.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-5 text-center text-gray-500">
                    No data found
                  </td>
                </tr>
              ) : (
                summary?.map((item, index) => (
                  <tr
                    key={item._id}
                    className="text-md group bg-white tracking-wide text-gray-700 hover:bg-gray-50 "
                  >
                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      {(page - 1) * limit + (index + 1)}
                    </td>

                    <td className="flex cursor-pointer items-center justify-between whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      <div className="flex flex-col items-center">
                        <img
                          src={item?.leader?.selected_Avatar}
                          alt="avatar"
                          className="mb-1 h-10 w-10 rounded-full object-cover"
                        />
                        <span className="text-center">
                          {item?.gangInfo?.groupLanguage}
                        </span>
                      </div>

                      <span className="ml-4 min-w-[140px]">
                        <span className="font-bold capitalize text-navy-700">
                          {item.leader?.nickName}
                        </span>
                        <br />
                        <span>+{item?.leader?.phone}</span>
                        <br />
                        <span className="font-bold capitalize text-navy-700">
                          {item?.gangInfo?.groupName}
                        </span>
                      </span>
                    </td>

                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      <div className="flex flex-col">
                        <span>
                          Active Hours :{" "}
                          {minutesToHM(Math.round(item?.totalActiveHours))}
                        </span>

                        <span>Points : {item?.totalHoursRank}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      <div className="flex flex-col">
                        <span>
                          Games Completed : {item?.totalGamesCompleted}
                        </span>

                        <span>Points : {item?.totalGamesRank}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      <div className="flex flex-col">
                        <span>Gifts Received : {item?.totalGiftsReceived}</span>

                        <span>Points: {item?.totalGiftsRank}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      {item.totalRanksEarned}
                    </td>
                    <td className="whitespace-nowrap border border-[#ddd9db] px-4 py-4">
                      <div className="flex items-center justify-between">
                        {currentTabPermissions?.edit &&
                          (actionLoader?.loading &&
                          actionLoader.index === index ? (
                            <img src={Loader} alt="" className="h-8 w-8" />
                          ) : item.gangInfo?.isHeartVerified ? (
                            <FaHeartCircleMinus
                              color="#c51369"
                              size={20}
                              className="cursor-pointer hover:text-[#a10f5b]"
                              onClick={() =>
                                updateHeartVerification(
                                  false,
                                  item?.gangInfo._id,
                                  index
                                )
                              }
                            />
                          ) : (
                            <TbHeartPlus
                              color="#c51369"
                              size={20}
                              className="cursor-pointer hover:text-[#a10f5b]"
                              onClick={() =>
                                updateHeartVerification(
                                  true,
                                  item?.gangInfo._id,
                                  index
                                )
                              }
                            />
                          ))}

                        <FaRegEye
                          color="#c51369"
                          size={20}
                          className="cursor-pointer hover:text-[#a10f5b]"
                          onClick={() => {
                            setActivityModal(() => ({
                              open: true,
                              gangInfo: item?.gangInfo,
                            }));
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {/* <div className="mt-4 flex items-center justify-center gap-4">
          <button
            className="rounded border p-2 disabled:opacity-50"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <FaChevronLeft />
          </button>

          <span className="font-medium">
            Page {page} of {totalPages}
          </span>

          <button
            className="rounded border p-2 disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            <FaChevronRight />
          </button>
        </div> */}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(page) => setPage(page)}
        />
      </div>
      {activityModal.open && activityModal.gangInfo && (
        <GangActivityModal
          open={activityModal.open && activityModal.gangInfo}
          onClose={() =>
            setActivityModal(() => ({
              open: false,
              gangInfo: null,
            }))
          }
          gangInfo={activityModal.gangInfo}
        />
      )}
    </div>
  );
};

export default GangSummary;
